const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const SEARCH_RADIUS_METERS = 120;
const cache = new Map();

const isClosed = (points) => points.length >= 4 &&
  Math.abs(points[0][0] - points[points.length - 1][0]) < 1e-8 &&
  Math.abs(points[0][1] - points[points.length - 1][1]) < 1e-8;
const geometry = (items) => (items || [])
  .map((point) => [Number(point.lon), Number(point.lat)])
  .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

export async function loadNearbyBuildingLines(latitude, longitude, signal) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
  const key = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
  if (cache.has(key)) return cache.get(key);
  const latDelta = SEARCH_RADIUS_METERS / 111320;
  const lngDelta = SEARCH_RADIUS_METERS /
    (111320 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.2));
  const bbox = [latitude - latDelta, longitude - lngDelta, latitude + latDelta, longitude + lngDelta].join(',');
  const response = await fetch(OVERPASS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: new URLSearchParams({
      data: `[out:json][timeout:15];(way["building"](${bbox});relation["building"](${bbox}););out geom;`,
    }),
    signal,
  });
  if (!response.ok) throw new Error(`Overpass request failed (${response.status})`);
  const data = await response.json();
  const lines = (data.elements || []).flatMap((element) => {
    if (element.type === 'way') {
      const points = geometry(element.geometry);
      return points.length >= 2 ? [{ id: `way-${element.id}`, points, isClosed: isClosed(points) }] : [];
    }
    if (element.type === 'relation') {
      return (element.members || []).filter((member) => member.type === 'way' && member.role !== 'inner')
        .map((member, index) => {
          const points = geometry(member.geometry);
          return { id: `relation-${element.id}-${member.ref || index}`, points, isClosed: isClosed(points) };
        }).filter((line) => line.points.length >= 2);
    }
    return [];
  });
  cache.set(key, lines);
  return lines;
}

const localMeters = ([lng, lat], referenceLatitude) => [
  lng * 111320 * Math.cos((referenceLatitude * Math.PI) / 180),
  lat * 111320,
];

export function findBuildingSnapPoint(point, lines, maxDistanceMeters = 3) {
  const [pointX, pointY] = localMeters(point, point[1]);
  let nearest = null;
  lines.forEach((line) => {
    for (let index = 0; index < line.points.length - 1; index += 1) {
      const start = line.points[index];
      const end = line.points[index + 1];
      const [startX, startY] = localMeters(start, point[1]);
      const [endX, endY] = localMeters(end, point[1]);
      const dx = endX - startX;
      const dy = endY - startY;
      const lengthSquared = dx * dx + dy * dy;
      const projection = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
        ((pointX - startX) * dx + (pointY - startY) * dy) / lengthSquared));
      const distance = Math.hypot(pointX - (startX + projection * dx), pointY - (startY + projection * dy));
      if (!nearest || distance < nearest.distance) {
        nearest = {
          distance,
          point: [
            start[0] + projection * (end[0] - start[0]),
            start[1] + projection * (end[1] - start[1]),
          ],
        };
      }
    }
  });
  return nearest && nearest.distance <= maxDistanceMeters ? nearest : null;
}
